"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useReviewDeliveryData = void 0;
var react_1 = require("react");
var common_utils_1 = require("@fdha/common-utils");
var graphql_api_patient_1 = require("@fdha/graphql-api-patient");
var MealDeliveryProvider_1 = require("./MealDeliveryProvider");
var MealDeliveryProvider_2 = require("./MealDeliveryProvider");
/**
 * Specialized hook for ReviewDelivery screen
 * Provides data and actions specifically formatted for the review delivery use case
 * This is the common version that can be used by both web and mobile
 *
 * @param showWeekMenu - When true, enables editing mode for week menu context,
 *                       allowing meal selection regardless of delivery status
 */
var useReviewDeliveryData = function (showWeekMenu) {
    var _a = (0, MealDeliveryProvider_1.useMealDeliveryContext)(), state = _a.state, actions = _a.actions, bridge = _a.bridge;
    var selections = (0, MealDeliveryProvider_2.useSelections)();
    var counts = (0, MealDeliveryProvider_2.useMealDeliveryCounts)();
    var hasSides = state.snacksAndSides.length > 0;
    // Determine if delivery is open or closed
    var isOpen = state.deliveryStatus === bridge.DeliveryStatus.Open;
    // Process closed delivery data when available
    var closedDeliveryOptions = (0, react_1.useMemo)(function () {
        if (isOpen || !state.closedDeliveryData) {
            return null;
        }
        var dishes = (0, common_utils_1.groupMenuOptionsByMealOptionType)(state.closedDeliveryData);
        var entreeDishes = (0, common_utils_1.filterDishes)((dishes === null || dishes === void 0 ? void 0 : dishes.entreeDishes) || {});
        var snacks = (0, common_utils_1.filterDishes)((dishes === null || dishes === void 0 ? void 0 : dishes.snacks) || {});
        var entreeFlattenedDishes = (0, common_utils_1.flattenDishes)(entreeDishes);
        var snackFlattenedDishes = (0, common_utils_1.flattenDishes)(snacks);
        // Separate prepared meals from meal kits for web compatibility
        var preparedMeals = entreeFlattenedDishes.filter(function (item) { return item.mealOptionType !== bridge.MealOptionType.MealKit; });
        var mealkits = entreeFlattenedDishes.filter(function (item) { return item.mealOptionType === bridge.MealOptionType.MealKit; });
        var entreesSelected = (0, common_utils_1.getQuantitySelected)(entreeFlattenedDishes);
        var snacksSelected = (0, common_utils_1.getQuantitySelected)(snackFlattenedDishes);
        return {
            preparedMeals: preparedMeals,
            mealkits: mealkits,
            snacksAndSides: snackFlattenedDishes,
            entreeDisplay: {
                selected: entreesSelected,
                total: entreesSelected,
            },
            snacksDisplay: {
                selected: snacksSelected,
                total: snacksSelected,
            },
            hasSelections: entreesSelected > 0 || snacksSelected > 0,
        };
    }, [isOpen, state.closedDeliveryData, bridge.MealOptionType]);
    var groupedSelections = (0, react_1.useMemo)(function () {
        if (isOpen) {
            // Use current selections for open delivery
            var grouped_1 = (0, common_utils_1.groupMenuOptionsByMealOptionType)({
                breakfast: selections.entrees,
                snacks: selections.snacksAndSides,
            });
            return {
                entreeDishes: (grouped_1 === null || grouped_1 === void 0 ? void 0 : grouped_1.entreeDishes) || {},
                snacks: (grouped_1 === null || grouped_1 === void 0 ? void 0 : grouped_1.snacks) || {},
            };
        }
        // Use closed delivery data for closed delivery
        if (!state.closedDeliveryData) {
            return {
                entreeDishes: {},
                snacks: {},
            };
        }
        var grouped = (0, common_utils_1.groupMenuOptionsByMealOptionType)(state.closedDeliveryData);
        return {
            entreeDishes: (grouped === null || grouped === void 0 ? void 0 : grouped.entreeDishes) || {},
            snacks: (grouped === null || grouped === void 0 ? void 0 : grouped.snacks) || {},
        };
    }, [isOpen, selections, state.closedDeliveryData]);
    // Helper function to get selections for mobile compatibility
    var getSelections = (0, react_1.useCallback)(function (type) {
        if (isOpen) {
            return type === 'entrees'
                ? groupedSelections.entreeDishes
                : groupedSelections.snacks;
        }
        var mealSelections = type === 'entrees'
            ? {
                breakfast: __spreadArray(__spreadArray([], ((closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.preparedMeals) || []), true), ((closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.mealkits) || []), true),
                snacks: [],
            }
            : {
                breakfast: [],
                snacks: (closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.snacksAndSides) || [],
            };
        var grouped = (0, common_utils_1.groupMenuOptionsByMealOptionType)(mealSelections);
        return type === 'entrees'
            ? (grouped === null || grouped === void 0 ? void 0 : grouped.entreeDishes) || {}
            : (grouped === null || grouped === void 0 ? void 0 : grouped.snacks) || {};
    }, [isOpen, groupedSelections, closedDeliveryOptions]);
    // Web-compatible properties - separate prepared meals from mealkits
    var _b = (0, react_1.useMemo)(function () {
        var prepared = [];
        var kits = [];
        selections.entrees.forEach(function (item) {
            if (item.mealOptionType === graphql_api_patient_1.MealOptionType.MealKit) {
                kits.push(item);
            }
            else {
                prepared.push(item);
            }
        });
        return { preparedMeals: prepared, mealkits: kits };
    }, [selections.entrees]), preparedMeals = _b.preparedMeals, mealkits = _b.mealkits;
    // Pre-compute variant counts for each meal kit
    var variantCounts = (0, react_1.useMemo)(function () {
        var counts = {};
        var allMenuItems = __spreadArray(__spreadArray([], state.entrees, true), state.snacksAndSides, true);
        allMenuItems.forEach(function (item) {
            if (item.mealOptionType === graphql_api_patient_1.MealOptionType.MealKit && item.menuOptionId) {
                counts[item.menuOptionId] = (counts[item.menuOptionId] || 0) + 1;
            }
        });
        return counts;
    }, [state.entrees, state.snacksAndSides]);
    // Helper function to check if a meal kit dish has multiple variants
    var hasMultipleVariants = (0, react_1.useCallback)(function (menuOptionId) {
        return (variantCounts[menuOptionId] || 0) > 1;
    }, [variantCounts]);
    // Web-compatible metadata
    var metadata = (0, react_1.useMemo)(function () { return ({
        amountPerDay: state.amountPerDay,
        snackAmount: state.snackAmount,
        amount: state.amount,
    }); }, [state.amountPerDay, state.snackAmount, state.amount]);
    if (isOpen || showWeekMenu) {
        return {
            // Mobile-compatible properties
            entreeSelections: getSelections('entrees'),
            snacksAndSidesSelections: getSelections('snacks'),
            // Web-compatible properties - use closed delivery data if available
            preparedMeals: preparedMeals,
            mealkits: mealkits,
            snacksAndSides: selections.snacksAndSides,
            metadata: metadata,
            isSaving: state.isSaving,
            error: state.error,
            // Use common counts logic - use closed delivery data if available
            requiredEntrees: counts.maxEntrees,
            requiredSnacks: counts.maxSnacksAndSides,
            selectedEntreesCount: counts.selectedEntreesCount,
            selectedSnacksAndSidesCount: counts.selectedSnacksAndSidesCount,
            // Display helpers - use closed delivery data if available
            entreeDisplay: counts.entreeDisplay,
            snacksDisplay: counts.snacksDisplay,
            isDirty: state.isDirty,
            // Backward compatibility properties for the screen
            loading: state.loading,
            hasSides: hasSides,
            isValid: counts.isValid, // Closed deliveries are always valid
            selections: groupedSelections,
            // Delivery status properties
            isOpen: isOpen,
            deliveryStatus: state.deliveryStatus,
            isCanceled: state.isCanceled,
            closedDeliveryData: closedDeliveryOptions,
            // Actions
            updateSelection: actions.updateSelection,
            saveSelections: actions.saveSelections,
            // Variant helper
            hasMultipleVariants: hasMultipleVariants,
        };
    }
    return {
        // Mobile-compatible properties
        entreeSelections: getSelections('entrees'),
        snacksAndSidesSelections: getSelections('snacks'),
        // Web-compatible properties - use closed delivery data if available
        preparedMeals: (closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.preparedMeals) || [],
        mealkits: (closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.mealkits) || [],
        snacksAndSides: (closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.snacksAndSides) || [],
        metadata: metadata,
        isSaving: state.isSaving,
        error: state.error,
        // Use common counts logic - use closed delivery data if available
        requiredEntrees: (closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.entreeDisplay.total) || 0,
        requiredSnacks: (closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.snacksDisplay.total) || 0,
        selectedEntreesCount: (closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.entreeDisplay.selected) || 0,
        selectedSnacksAndSidesCount: (closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.snacksDisplay.selected) || 0,
        // Display helpers - use closed delivery data if available
        entreeDisplay: (closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.entreeDisplay) || {
            selected: 0,
            total: 0,
        },
        snacksDisplay: (closedDeliveryOptions === null || closedDeliveryOptions === void 0 ? void 0 : closedDeliveryOptions.snacksDisplay) || {
            selected: 0,
            total: 0,
        },
        isDirty: state.isDirty,
        // Backward compatibility properties for the screen
        loading: state.closedDeliveryLoading,
        hasSides: hasSides,
        isValid: true, // Closed deliveries are always valid
        selections: groupedSelections,
        // Delivery status properties
        isOpen: isOpen,
        deliveryStatus: state.deliveryStatus,
        isCanceled: state.isCanceled,
        closedDeliveryData: closedDeliveryOptions,
        // Actions
        updateSelection: actions.updateSelection,
        saveSelections: actions.saveSelections,
        // Variant helper
        hasMultipleVariants: hasMultipleVariants,
    };
};
exports.useReviewDeliveryData = useReviewDeliveryData;
